import React, { useEffect, useState } from "react";
import { Button } from "reactstrap";
import CustomisedModal from "../../../../components/Modal/CustomisedModal";
import { AddimageSvg, DeleteSvg, SaveSvg } from "../../../../components/CommonSvg";
import { ApiResponseMessage } from "../../../../helper/tosterHelper";
import { DANGER } from "../../../../constant/commonConstants";
import { vendorLogoUrl } from "../../../../utils/generalUtils";

export const LOGO_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/svg+xml",
];
const LOGO_MAX_SIZE = 2 * 1024 * 1024;

const readableSize = (bytes) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

function VendorLogoModal({ vendor, onClose, onUpload, onRemove }) {
  const [dragging, setDragging] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingUrl, setPendingUrl] = useState(null);
  const [picking, setPicking] = useState(!vendor.logo);

  useEffect(() => {
    return () => {
      if (pendingUrl) URL.revokeObjectURL(pendingUrl);
    };
  }, [pendingUrl]);

  const clearPending = () => {
    setPendingFile(null);
    setPendingUrl(null);
  };

  const handleFile = (file) => {
    if (!file) return;

    if (!LOGO_MIME_TYPES.includes(file.type)) {
      ApiResponseMessage("Logo must be a PNG, JPG, WEBP or SVG file", DANGER);
      return;
    }
    if (file.size > LOGO_MAX_SIZE) {
      ApiResponseMessage("Logo must be 2 MB or smaller", DANGER);
      return;
    }

    setPendingFile(file);
    setPendingUrl(URL.createObjectURL(file));
  };

  const onSelectFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    handleFile(file);
  };

  const onDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    handleFile(event.dataTransfer.files?.[0]);
  };

  const onUploadClick = () => {
    if (!pendingFile) return;

    const formData = new FormData();
    formData.append("vendorId", vendor.id);
    formData.append("vendorLogo", pendingFile);
    onUpload(formData);
    onClose();
  };

  const showPending = Boolean(pendingFile);
  const showDropZone = !showPending && picking;

  return (
    <CustomisedModal
      modalView={true}
      customClass={"category-modal vendor_logo_modal"}
      modalName={
        <span className="vendor_log_modal_title">
         Vendor logo — {vendor.name}
        </span>
      }
      onModalDismiss={onClose}
    >
      <div className="vendor_logo_manage">
        {showDropZone && (
          <label
            className={`vendor_logo_drop${dragging ? " is_dragging" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
          >
            <AddimageSvg />
            <b>Drag &amp; drop a logo here, or click to browse</b>
            <span>PNG, JPG, WEBP or SVG · max 2 MB</span>
            <input
              type="file"
              accept={LOGO_MIME_TYPES.join(",")}
              onChange={onSelectFile}
            />
          </label>
        )}

        {showPending && (
          <>
            <div className="vendor_logo_stage is_pending">
              <img src={pendingUrl} alt={pendingFile.name} />
            </div>
            <div className="vendor_logo_picked">
              <b>{pendingFile.name}</b>
              <span>{readableSize(pendingFile.size)}</span>
           </div>
          </>
        )}

        {!showPending && !showDropZone && (
          <div className="vendor_logo_stage">
            <img src={vendorLogoUrl(vendor.logo)} alt={vendor.name} />
          </div>
        )}

        <div className="vendor_logo_foot">

          {showPending ? (
            <>
              <Button
                className="vendor_logo_close"
                onClick={() => {
                  clearPending();
                  setPicking(!vendor.logo);
                }}
                color="secondary"
              >
                Cancel
              </Button>
              <Button color="primary" className="vendor_logo_upload" onClick={onUploadClick}>
                <SaveSvg /> Upload
              </Button>
            </>
          ) : (
            <>
              {vendor.logo &&
                (picking ? (
                  <Button
                    className="vendor_logo_close"
                    color="secondary"
                    onClick={() => setPicking(false)}
                  >
                    Cancel
                  </Button>
                ) : (
                  <Button
                    className="vendor_logo_change"
                    color="primary"
                    onClick={() => setPicking(true)}
                  >
                    <AddimageSvg /> Change logo
                  </Button>
                ))}
              <Button className="vendor_logo_close" onClick={onClose}>
                Close
              </Button>
            </>
          )}
        </div>
      </div>
    </CustomisedModal>
  );
}

export default VendorLogoModal;
